import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Phone } from "lucide-react";
import { api } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUSES = ["confirmed", "present", "no_show", "completed"];

export default function BookingsPage() {
  const { t } = useTranslation();
  const [bookings, setBookings] = useState([]);
  const [poojas, setPoojas] = useState([]);
  const [filterDate, setFilterDate] = useState("");
  const [filterPooja, setFilterPooja] = useState("all");
  const [tab, setTab] = useState("upcoming"); // upcoming | all (Upcoming default per spec)

  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (filterDate) q.set("date", filterDate);
    if (filterPooja && filterPooja !== "all") q.set("pooja_slug", filterPooja);
    api.get(`/bookings?${q.toString()}`).then(({ data }) => setBookings(data));
  }, [filterDate, filterPooja]);

  useEffect(() => { api.get("/poojas").then(({ data }) => setPoojas(data)); }, []);
  useEffect(() => { load(); }, [load]);

  // Today in IST so the Upcoming tab matches operator's local context.
  const todayIst = useMemo(() => {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(new Date());
    const m = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    return `${m.year}-${m.month}-${m.day}`;
  }, []);

  const visible = useMemo(() => {
    if (tab === "upcoming") return bookings.filter((b) => b.booking_date >= todayIst);
    return bookings;
  }, [bookings, tab, todayIst]);

  const updateAttendance = async (id, value) => {
    try {
      await api.patch(`/bookings/${id}/attendance`, { attendance_status: value });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, attendance_status: value } : b)));
      toast.success("Attendance updated");
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const upcomingCount = bookings.filter((b) => b.booking_date >= todayIst).length;

  return (
    <div data-testid="admin-bookings-page">
      <h1 className="font-heading text-3xl">{t("admin.bookings")}</h1>

      {/* Tabs */}
      <div className="mt-5 inline-flex rounded-md border border-[color:var(--border)] bg-white overflow-hidden">
        <button
          onClick={() => setTab("upcoming")}
          data-testid="tab-upcoming"
          className={`px-4 py-2 text-sm ${tab === "upcoming" ? "bg-[color:var(--brand)] text-[#faf8f5]" : ""}`}
        >
          {t("admin.upcoming")} <span className="opacity-70 text-xs">({upcomingCount})</span>
        </button>
        <button
          onClick={() => setTab("all")}
          data-testid="tab-all"
          className={`px-4 py-2 text-sm border-l border-[color:var(--border)] ${tab === "all" ? "bg-[color:var(--brand)] text-[#faf8f5]" : ""}`}
        >
          {t("admin.all")} <span className="opacity-70 text-xs">({bookings.length})</span>
        </button>
      </div>

      {/* Existing filters preserved */}
      <div className="mt-4 flex flex-wrap gap-3 items-center">
        <input
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          className="rounded-md border border-[color:var(--border)] bg-white px-3 py-2 text-sm"
          data-testid="filter-date"
        />
        <Select value={filterPooja} onValueChange={setFilterPooja}>
          <SelectTrigger className="w-60" data-testid="filter-pooja"><SelectValue placeholder="All poojas" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Poojas</SelectItem>
            {poojas.map((p) => <SelectItem key={p.slug} value={p.slug}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={() => { setFilterDate(""); setFilterPooja("all"); }} className="px-3 py-2 text-sm border border-[color:var(--border)] rounded-md" data-testid="clear-filters">Clear</button>
      </div>

      <div className="mt-6 bg-white border border-[color:var(--border)] rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-[color:var(--bg-2)] text-left">
            <tr>
              <th className="p-3">{t("admin.devotee")}</th>
              <th className="p-3">{t("admin.date")}</th>
              <th className="p-3">{t("admin.pooja")}</th>
              <th className="p-3">{t("admin.mobile")}</th>
              <th className="p-3">{t("admin.payment")}</th>
              <th className="p-3">{t("admin.attendanceCol")}</th>
              <th className="p-3">{t("admin.ref")}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((b) => (
              <tr key={b.id} className="border-t border-[color:var(--border)]" data-testid={`booking-row-${b.id}`}>
                <td className="p-3 font-semibold" data-testid={`booking-name-${b.id}`}>{b.devotee_name}</td>
                <td className="p-3">{b.booking_date}</td>
                <td className="p-3">{b.pooja_name}</td>
                <td className="p-3">
                  <a href={`tel:${b.mobile}`} className="inline-flex items-center gap-1 text-[color:var(--brand)]" data-testid={`call-${b.id}`}><Phone size={12} />{b.mobile}</a>
                </td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${b.payment_status === "paid" ? "bg-green-100 text-green-800" : b.payment_status === "pending" ? "bg-amber-100 text-amber-800" : "bg-[color:var(--bg-2)]"}`}>{b.payment_status}</span>
                </td>
                <td className="p-3">
                  <Select value={b.attendance_status} onValueChange={(v) => updateAttendance(b.id, v)}>
                    <SelectTrigger className="w-40" data-testid={`attendance-${b.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`admin.attendance.${s}`)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-3 font-mono text-xs opacity-70">{b.reference}</td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td className="p-6 text-center opacity-65" colSpan={7}>{t("admin.noBookings")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
